import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ShieldCheck, Landmark, Zap, FileCheck, Briefcase, User, Handshake, Home, Car, ArrowRight, Phone, Mail, MapPin } from "lucide-react";
import { Logo } from "@/components/kit";
import { inr } from "@/lib/api";

function Emi() {
  const [p, setP] = useState(500000);
  const [r, setR] = useState(13);
  const [n, setN] = useState(36);
  const m = r / 1200;
  const emi = Math.round((p * m * (1 + m) ** n) / ((1 + m) ** n - 1));
  const Row = ({ l, v, set, min, max, step, fmt }) => (
    <label className="block">
      <div className="mb-1 flex justify-between text-xs font-semibold text-slate-600"><span>{l}</span><span className="text-slate-900">{fmt(v)}</span></div>
      <input type="range" min={min} max={max} step={step} value={v} onChange={(e) => set(Number(e.target.value))} className="w-full accent-blue-900" data-testid={`emi-${l.toLowerCase().replace(/\W+/g, "-")}`} />
    </label>
  );
  return (
    <div id="calculator" className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm" data-testid="emi-calculator">
      <h3 className="font-display text-lg font-semibold">EMI Calculator</h3>
      <div className="mt-4 space-y-4">
        <Row l="Loan Amount" v={p} set={setP} min={25000} max={5000000} step={25000} fmt={inr} />
        <Row l="Interest Rate" v={r} set={setR} min={9} max={30} step={0.5} fmt={(x) => `${x}%`} />
        <Row l="Tenure" v={n} set={setN} min={6} max={84} step={6} fmt={(x) => `${x} months`} />
      </div>
      <div className="mt-5 rounded-xl bg-slate-50 p-4">
        <p className="text-xs text-slate-500">Indicative monthly EMI</p>
        <p className="font-display text-3xl font-bold text-blue-900" data-testid="emi-result">{inr(emi)}</p>
        <p className="mt-1 text-[11px] text-slate-400">Actual rates are decided by the lender based on your profile.</p>
      </div>
    </div>
  );
}

export default function Landing() {
  const nav = useNavigate();
  const features = [
    [Landmark, "Multiple lenders, one application", "Compare offers from partner banks, NBFCs and fintechs."],
    [Zap, "Fast digital journey", "Mobile OTP, online KYC and document upload - no branch visits."],
    [ShieldCheck, "Consent-first & secure", "Your data is shared only with lenders you consent to."],
    [FileCheck, "Track every step", "Live status from application to disbursement."],
  ];
  return (
    <div className="min-h-screen bg-white">
      <header className="sticky top-0 z-40 border-b border-slate-200 bg-white/90 backdrop-blur-md">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-3 sm:px-6 lg:px-8">
          <Logo />
          <nav className="hidden items-center gap-6 text-sm font-medium text-slate-600 md:flex">
            <Link to="/apply/PL" data-testid="nav-personal-loan" className="hover:text-blue-900">Personal Loan</Link>
            <Link to="/apply/BL" data-testid="nav-business-loan" className="hover:text-blue-900">Business Loan</Link>
            <Link to="/apply/HL" data-testid="nav-home-loan" className="hover:text-blue-900">Home Loan</Link>
            <Link to="/apply/CL" data-testid="nav-car-loan" className="hover:text-blue-900">Car Loan</Link>
            <Link to="/partner/register" data-testid="nav-partner" className="hover:text-blue-900">Partner with Us (DSA)</Link>
            <a href="#calculator" className="hover:text-blue-900">Calculator</a>
          </nav>
          <div className="flex items-center gap-2">
            <button data-testid="landing-track-btn" onClick={() => nav("/login")} className="hidden h-9 rounded-md border border-slate-300 px-4 text-sm font-semibold sm:block">Track Application</button>
            <button data-testid="landing-login-btn" onClick={() => nav("/login")} className="h-9 rounded-md bg-blue-900 px-4 text-sm font-semibold text-white hover:bg-blue-800">Login</button>
          </div>
        </div>
      </header>

      <section className="mx-auto grid max-w-7xl gap-12 px-4 py-14 sm:px-6 md:py-20 lg:grid-cols-12 lg:px-8">
        <div className="lg:col-span-7">
          <span className="inline-flex items-center gap-2 rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-700">
            <ShieldCheck className="h-3.5 w-3.5" /> Loan Service Provider · Multi-lender marketplace
          </span>
          <h1 className="mt-5 font-display text-4xl font-extrabold leading-tight tracking-tight text-slate-900 sm:text-5xl lg:text-6xl">
            The right loan, from the <span className="text-blue-900">right lender.</span>
          </h1>
          <p className="mt-5 max-w-xl text-base text-slate-600 md:text-lg">
            Apply once for a Personal, Business, Home or Car Loan. FinOrbit matches your profile with partner lenders and brings you their offers - you choose.
          </p>
          <div className="mt-8 grid max-w-xl gap-3 sm:grid-cols-2">
            <button data-testid="apply-pl-btn" onClick={() => nav("/apply/PL")} className="group flex items-center justify-between rounded-xl bg-blue-900 p-5 text-left text-white hover:bg-blue-800">
              <div><User className="mb-2 h-5 w-5" /><p className="font-display text-lg font-semibold">Personal Loan</p><p className="text-xs text-blue-200">₹25K – ₹40L · Salaried & self-employed</p></div>
              <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
            </button>
            <button data-testid="apply-bl-btn" onClick={() => nav("/apply/BL")} className="group flex items-center justify-between rounded-xl border-2 border-slate-900 p-5 text-left hover:bg-slate-50">
              <div><Briefcase className="mb-2 h-5 w-5" /><p className="font-display text-lg font-semibold">Business Loan</p><p className="text-xs text-slate-500">₹1L – ₹1Cr · MSMEs & traders</p></div>
              <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
            </button>
            <button data-testid="apply-hl-btn" onClick={() => nav("/apply/HL")} className="group flex items-center justify-between rounded-xl border-2 border-slate-900 p-5 text-left hover:bg-slate-50">
              <div><Home className="mb-2 h-5 w-5 text-blue-900" /><p className="font-display text-lg font-semibold">Home Loan</p><p className="text-xs text-slate-500">Purchase, construction & balance transfer</p></div>
              <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
            </button>
            <button data-testid="apply-cl-btn" onClick={() => nav("/apply/CL")} className="group flex items-center justify-between rounded-xl bg-slate-900 p-5 text-left text-white hover:bg-slate-800">
              <div><Car className="mb-2 h-5 w-5 text-blue-400" /><p className="font-display text-lg font-semibold">Car Loan</p><p className="text-xs text-slate-400">New & used cars · up to 84 months</p></div>
              <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
            </button>
          </div>
          <p className="mt-4 text-xs text-slate-400">Checking offers does not guarantee approval. Lenders take the final credit decision.</p>
        </div>
        <div className="space-y-6 lg:col-span-5">
          <img src="/images/hero.jpg" alt="Business owner at work" className="h-64 w-full rounded-2xl object-cover object-top" />
          <Emi />
        </div>
      </section>

      <section className="border-y border-slate-100 bg-slate-50">
        <div className="mx-auto grid max-w-7xl gap-6 px-4 py-14 sm:grid-cols-2 sm:px-6 lg:grid-cols-4 lg:px-8">
          {features.map(([Icon, t, s]) => (
            <div key={t} className="rounded-xl border border-slate-200 bg-white p-6">
              <Icon className="h-6 w-6 text-blue-900" />
              <h3 className="mt-4 font-display font-semibold">{t}</h3>
              <p className="mt-1 text-sm text-slate-500">{s}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-14 sm:px-6 lg:px-8">
        <div className="flex flex-col items-start justify-between gap-6 rounded-2xl bg-slate-900 p-8 text-white md:flex-row md:items-center md:p-12">
          <div>
            <Handshake className="h-7 w-7 text-emerald-400" />
            <h2 className="mt-3 font-display text-2xl font-bold md:text-3xl">Become a FinOrbit DSA partner</h2>
            <p className="mt-2 max-w-lg text-sm text-slate-300">Submit customer applications, track sanctions in real time and get transparent payouts.</p>
            <p className="mt-1 text-xs text-slate-400">Partnership enquiries: partnership@finorbitfinance.com</p>
          </div>
          <div className="flex gap-3">
            <button data-testid="dsa-register-cta" onClick={() => nav("/partner/register")} className="h-11 rounded-md bg-emerald-500 px-6 text-sm font-semibold text-slate-900 hover:bg-emerald-400">Register as DSA</button>
            <button data-testid="dsa-login-cta" onClick={() => nav("/login?tab=partner")} className="h-11 rounded-md border border-slate-600 px-6 text-sm font-semibold hover:bg-slate-800">Partner Login</button>
          </div>
        </div>
      </section>

      <footer className="border-t border-slate-200 bg-white">
        <div className="mx-auto grid max-w-7xl gap-8 px-4 py-10 text-sm text-slate-600 sm:px-6 md:grid-cols-3 lg:px-8">
          <div>
            <Logo />
            <p className="mt-3 text-xs leading-relaxed text-slate-500">FinOrbit Finance Pvt Ltd is a Loan Service Provider (LSP) / loan distribution technology platform and is not an NBFC or bank. Loans are sanctioned and disbursed solely by partner lenders (Regulated Entities).</p>
          </div>
          <div className="space-y-2 text-xs">
            <p className="flex items-start gap-2"><MapPin className="mt-0.5 h-4 w-4 shrink-0" />1009, Sakar 5, Bh-Croma, Opp. H.K. College, Ashram Road, Ahmedabad, Gujarat – 380009</p>
            <p className="flex items-center gap-2"><Phone className="h-4 w-4" />+91-9824053365</p>
            <p className="flex items-center gap-2"><Mail className="h-4 w-4" />info@finorbitfinance.com</p>
            <p className="flex items-center gap-2" data-testid="partnership-email"><Handshake className="h-4 w-4" />Partnerships: partnership@finorbitfinance.com</p>
          </div>
          <div className="space-y-1 text-xs">
            <p className="font-semibold text-slate-800">Grievance Redressal</p>
            <p>Email: info@finorbitfinance.com · Phone: +91-9824053365</p>
            <p>CIN: U64990GJ2026PTC177553</p>
            <p className="pt-2 text-slate-400">© {new Date().getFullYear()} FinOrbit Finance Pvt Ltd · www.finorbitfinance.com</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
